import { Link } from "@tanstack/react-router";

import { usePublicDirectory } from "../hooks/use-public-directory";
import { servicePath, sitePath, useSiteConfig } from "../hooks/use-site-config";
import { card } from "./section";
import { SiteBrand } from "./site-head";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";
import { appConfig } from "@complete-athlete/enums";

const columnTitle = "mb-3.5 text-[13px] font-medium text-muted-foreground";
const columnLink = "block py-[5px] text-sm font-medium transition-colors hover:text-muted-foreground";

/** Services the footer lists before it stops. */
const LISTED = 6;

/**
 * The page's last card: the trainer's services, the other pages, and how to reach them. Each
 * column is there only when the trainer has something to put in it.
 */
export const SiteFooter = () => {
  const site = useSiteConfig();
  // The same request the page above made, so this reads the cache rather than asking again.
  const { directory } = usePublicDirectory({ days: site.calendarDays });
  const services = directory?.services.slice(0, LISTED) ?? [];

  return (
    <footer className={cn(card, "mt-3 px-6 pt-8 pb-5 md:px-10 md:pt-11 md:pb-[26px]")}>
      <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-[1.4fr_1fr_1fr_1.2fr]">
        <div>
          <SiteBrand />
          {directory ? (
            <p className="mt-3.5 max-w-[260px] text-sm text-muted-foreground">{directory.business.name}</p>
          ) : null}
        </div>
        {services.length > 0 ? (
          <div>
            <h4 className={columnTitle}>{m.footer_services()}</h4>
            {services.map((service) => (
              <Link key={service.id} to={servicePath(site.slug, service.id)} className={columnLink}>
                {service.name}
              </Link>
            ))}
          </div>
        ) : null}
        <div>
          <h4 className={columnTitle}>{m.footer_studio()}</h4>
          <Link to={sitePath(site.slug, "coaches")} className={columnLink}>
            {m.landing_nav_coaches()}
          </Link>
          <Link to={sitePath(site.slug, "pricing")} className={columnLink}>
            {m.landing_nav_pricing()}
          </Link>
          <Link to={sitePath(site.slug, "contact")} className={columnLink}>
            {m.landing_nav_contact()}
          </Link>
          <Link to="/login" className={columnLink}>
            {m.landing_nav_sign_in()}
          </Link>
        </div>
        {site.phone || site.address ? (
          <div>
            {site.phone ? (
              <>
                <h4 className={columnTitle}>{m.footer_front_desk()}</h4>
                <a href={site.phoneHref} className="block text-[28px] leading-tight font-medium tracking-[-0.03em]">
                  {site.phone}
                </a>
              </>
            ) : null}
            {site.address ? <p className="mt-3.5 text-sm text-muted-foreground">{site.address}</p> : null}
          </div>
        ) : null}
      </div>
      <div className="mt-11 flex flex-col justify-between gap-1.5 border-t border-[var(--site-dot)] pt-5 text-[13px] text-muted-foreground sm:flex-row">
        {site.email ? <a href={`mailto:${site.email}`}>{site.email}</a> : <span />}
        <span className="flex flex-wrap gap-4">
          {/* The bar carries the trainer's name, so the platform says itself only here, quietly. */}
          {site.brand ? <Link to="/">{m.footer_powered_by({ name: appConfig.name })}</Link> : null}
          <Link to="/privacy">{m.footer_privacy()}</Link>
          <Link to="/terms">{m.footer_terms()}</Link>
          <Link to="/consumer-health-data">{m.footer_health_data()}</Link>
          <Link to="/legal">{m.footer_legal()}</Link>
        </span>
      </div>
    </footer>
  );
};
